/**
 * Server-issued anonymous device identity for the chat quota.
 *
 * The id is generated and signed by the server (POST chat/devices/), so the
 * client cannot mint one to reset its allowance; issuance is capped per IP.
 * Stored in localStorage; falls back to a per-load identity when storage is
 * blocked (the user simply provisions again next visit).
 */
import axios from 'axios';

import { BASE_URL, V2 } from '@/api/axios';

const KEY = 'recovry_chat_device';

export interface ChatDevice {
  id: string;
  token: string;
}

let cached: ChatDevice | null = null;
let provisioning: Promise<ChatDevice> | null = null;

function readStored(): ChatDevice | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ChatDevice>;
    return typeof parsed.id === 'string' && typeof parsed.token === 'string'
      ? { id: parsed.id, token: parsed.token }
      : null;
  } catch {
    return null;
  }
}

async function provision(): Promise<ChatDevice> {
  // Plain axios: the shared instance's 401 interceptor must never fire here.
  const { data } = await axios.post<{ device_id: string; device_token: string }>(
    `${BASE_URL}${V2}chat/devices/`,
  );
  const device = { id: data.device_id, token: data.device_token };
  try {
    localStorage.setItem(KEY, JSON.stringify(device));
  } catch {
    /* private mode: per-load identity */
  }
  return device;
}

/** Current device identity, provisioning one on first use. Single-flight. */
export async function getDevice(): Promise<ChatDevice> {
  if (cached) return cached;
  const stored = readStored();
  if (stored) {
    cached = stored;
    return stored;
  }
  if (!provisioning) {
    provisioning = provision()
      .then((d) => {
        cached = d;
        return d;
      })
      .finally(() => {
        provisioning = null;
      });
  }
  return provisioning;
}

/** Forget the identity (server said `invalid_device`, e.g. after a key rotation). */
export function resetDevice(): void {
  cached = null;
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
